import { useState } from 'react';
import { SERVICES } from '../data/content';
import { Service } from '../types';
import {
  Search,
  Target,
  Feather,
  Newspaper,
  ShieldAlert,
  Cpu,
  CheckCircle2,
  ArrowRight,
  X,
  ChevronRight
} from 'lucide-react';

interface ServicesSectionProps {
  onOpenDiagnostic: (serviceId?: string) => void;
}

export default function ServicesSection({ onOpenDiagnostic }: ServicesSectionProps) {
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const getServiceConfig = (id: string) => {
    switch (id) {
      case 'posicionamiento-digital':
        return {
          icon: <Search className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />,
          bgIcon: 'bg-cyan-50 dark:bg-slate-800',
          topBorder: 'border-t-4 border-cyan-500',
        };
      case 'comunicacion-estrategica':
        return {
          icon: <Target className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />,
          bgIcon: 'bg-emerald-50 dark:bg-slate-800',
          topBorder: 'border-t-4 border-emerald-600',
        };
      case 'produccion-de-contenidos':
        return {
          icon: <Feather className="w-5 h-5 text-rose-600 dark:text-rose-400" />,
          bgIcon: 'bg-rose-50 dark:bg-slate-800',
          topBorder: 'border-t-4 border-rose-500',
        };
      case 'gestion-de-medios':
        return {
          icon: <Newspaper className="w-5 h-5 text-purple-600 dark:text-purple-400" />,
          bgIcon: 'bg-purple-50 dark:bg-slate-800',
          topBorder: 'border-t-4 border-purple-600',
        };
      case 'reputacion-y-crisis':
        return {
          icon: <ShieldAlert className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
          bgIcon: 'bg-blue-50 dark:bg-slate-800',
          topBorder: 'border-t-4 border-blue-600',
        };
      case 'ia-aplicada':
        return {
          icon: <Cpu className="w-5 h-5 text-teal-600 dark:text-teal-400" />,
          bgIcon: 'bg-teal-50 dark:bg-slate-800',
          topBorder: 'border-t-4 border-teal-600',
        };
      default:
        return {
          icon: <Target className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />,
          bgIcon: 'bg-cyan-50 dark:bg-slate-800',
          topBorder: 'border-t-4 border-cyan-500',
        };
    }
  };

  return (
    <section id="servicios" className="py-20 md:py-28 bg-[#f8f7f4] dark:bg-[#0b131e] text-slate-900 dark:text-white relative transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-left max-w-3xl space-y-4">
          <p className="text-xs font-bold text-cyan-700 dark:text-cyan-400 tracking-widest uppercase">QUÉ HACEMOS</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
            Comunicación con propósito, <br className="hidden sm:inline" />
            resultados con estrategia
          </h2>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
            Acompañamos a organizaciones, marcas y líderes a comunicar mejor: con foco, con datos y con historias que construyen autoridad. Estos son algunos de los servicios que ponemos a tu disposición.
          </p>
        </div>

        {/* Services Grid (6 Cards) */}
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((service) => {
            const config = getServiceConfig(service.id);
            return (
              <div
                key={service.id}
                className={`group relative rounded-3xl bg-white dark:bg-slate-900 p-5 sm:p-8 shadow-sm hover:shadow-xl border border-slate-200/80 dark:border-slate-800 ${config.topBorder} flex flex-col justify-between transition-all duration-300 hover:-translate-y-1`}
              >
                <div>
                  {/* Icon Box */}
                  <div className={`w-12 h-12 rounded-2xl ${config.bgIcon} flex items-center justify-center`}>
                    {config.icon}
                  </div>

                  {/* Title & Description */}
                  <h3 className="mt-6 text-xl font-extrabold text-slate-900 dark:text-white">
                    {service.title}
                  </h3>
                  <p className="mt-3 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    {service.shortDesc}
                  </p>

                  {/* Key deliverables list */}
                  <div className="mt-6 space-y-2 border-t border-slate-100 dark:border-slate-800 pt-4">
                    {service.deliverables.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card Action */}
                <div className="mt-8 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <button
                    onClick={() => setSelectedService(service)}
                    className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-cyan-600 dark:group-hover:text-cyan-400 flex items-center gap-1 transition-colors"
                  >
                    <span>Ver detalle del servicio</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <button
                    onClick={() => onOpenDiagnostic(service.id)}
                    className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-cyan-600 dark:hover:bg-cyan-500 text-slate-700 dark:text-slate-200 hover:text-white transition-all"
                    title="Agendar para este servicio"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Service Detail Modal */}
      {selectedService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
          <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setSelectedService(null)}
              className="absolute top-5 right-5 p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4">
              <span className="inline-block px-3 py-1 rounded-full bg-cyan-100 dark:bg-cyan-950 text-cyan-800 dark:text-cyan-300 font-bold text-xs uppercase tracking-wider">
                DETALLE DE SERVICIO
              </span>

              <h3 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">{selectedService.title}</h3>

              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{selectedService.fullDesc}</p>

              {/* Deliverables */}
              <div className="pt-4 space-y-2">
                <p className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Entregables Clave:</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {selectedService.deliverables.map((deliv, idx) => (
                    <div key={idx} className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700 flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200">
                      <CheckCircle2 className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                      <span>{deliv}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ideal For */}
              <div className="pt-2 p-3 rounded-xl bg-cyan-50 dark:bg-cyan-950/40 border border-cyan-200 dark:border-cyan-800 text-xs">
                <span className="font-bold text-cyan-900 dark:text-cyan-300 uppercase">Ideal para: </span>
                <span className="text-cyan-800 dark:text-cyan-200">{selectedService.idealFor}</span>
              </div>

              {/* Modal CTA */}
              <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={() => setSelectedService(null)}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 font-bold text-xs"
                >
                  Cerrar
                </button>
                <button
                  onClick={() => {
                    const id = selectedService.id;
                    setSelectedService(null);
                    onOpenDiagnostic(id);
                  }}
                  className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-xs shadow-md"
                >
                  Solicitar Propuesta
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
